/**
 * Device-owner verification through a WebAuthn platform authenticator.
 *
 * What this adapter is claiming, stated plainly, because the whole Journal Lock
 * feature rests on it:
 *
 * - `navigator.credentials.get({ userVerification: "required" })` against a
 *   platform authenticator makes the browser run the OS user-verification
 *   gesture — Touch ID, Windows Hello, a device passcode. If it resolves, the
 *   platform verified the owner. That is the same single bit the native
 *   `LAPolicy.deviceOwnerAuthentication` call returned, and it is all Journal
 *   Lock ever needed.
 * - There is **no server**, so nobody verifies the assertion signature or the
 *   challenge. This is not a cryptographic authentication of a remote party; it
 *   is a local gesture gate, exactly as the native one was. Treating it as more
 *   than that in the copy would be a false claim (`02-ARCHITECTURE.md` §6.6).
 * - Biometric data is never exposed to the page. That claim survives the port
 *   intact and is the one native privacy statement that ports verbatim.
 *
 * What does **not** port: `isUserVerifyingPlatformAuthenticatorAvailable()`
 * answers one boolean, so the four-way Face ID / Touch ID / Optic ID / passcode
 * labelling is unrecoverable. This adapter resolves to `"passcode"` — rendered
 * as "Device owner" — or `"unavailable"`, and never infers a modality from a
 * user agent string; `planJournalLockChange` decides what is permitted from
 * this value and must not be deciding it on a guess.
 *
 * Also unrecoverable: the reason string. `JOURNAL_AUTH_REASONS` supplies copy
 * that the native prompt displayed inside the system sheet, and WebAuthn's
 * dialog copy is fixed by the browser. The reason still reaches the curtain, so
 * the user is told what the prompt is for by the surface that raised it — but
 * the system sheet itself will not repeat it.
 */

import type { StringKeyValueStore } from "@ff/domain/ports";
import type {
  DeviceVerificationPort,
  JournalAuthReason,
  WebVerificationAvailability,
} from "@ff/domain/ports";
import type { ByteArray } from "./bytes";
import { decodeBase64Url, encodeBase64Url } from "./bytes";

/**
 * Where the enrolled credential id lives.
 *
 * Not a §3.1 preference — it is adapter bookkeeping — but it must be shared by
 * every tab of the profile, which is why it is a `StringKeyValueStore` and not
 * scene storage. The value is a public credential identifier: it authenticates
 * nothing on its own, and losing it costs a re-enrolment, not access.
 */
export const WEBAUTHN_CREDENTIAL_KEY = "journal.lock.webauthnCredential";

/** The account name the platform shows in its own credential manager. */
export const WEBAUTHN_USER_NAME = "Fighting Form journal";
export const WEBAUTHN_RELYING_PARTY_NAME = "Fighting Form";

/** Long enough that a user reaching for a fingerprint reader is not rushed. */
export const WEBAUTHN_TIMEOUT_MS = 60_000;

/** ES256 first, RS256 as the fallback every platform authenticator supports. */
const CREDENTIAL_ALGORITHMS: readonly PublicKeyCredentialParameters[] = [
  { type: "public-key", alg: -7 },
  { type: "public-key", alg: -257 },
];

export interface WebAuthnCredentialContainer {
  create(options: CredentialCreationOptions): Promise<Credential | null>;
  get(options: CredentialRequestOptions): Promise<Credential | null>;
}

export interface WebAuthnEnvironment {
  /** `undefined` on a page with no WebAuthn at all — plain HTTP, or an old browser. */
  readonly credentials: WebAuthnCredentialContainer | undefined;
  /**
   * `undefined` when `PublicKeyCredential` is absent. Kept as a nullable
   * function rather than a boolean so the probe runs at the moment it is asked:
   * an authenticator can be enrolled or removed while the tab is open, and a
   * value captured at construction would strand the user who just fixed it.
   */
  readonly isUserVerifyingPlatformAuthenticatorAvailable:
    | (() => Promise<boolean>)
    | undefined;
  randomBytes(length: number): ByteArray;
  /**
   * Left `undefined` in the browser so the user agent uses the current origin's
   * effective domain. Pinning it would break every deployment on a host the
   * constant did not anticipate, and a mismatched id is a hard failure with no
   * diagnostic the user could act on.
   */
  readonly relyingPartyId: string | undefined;
}

export function browserWebAuthnEnvironment(): WebAuthnEnvironment {
  const publicKeyCredential = (
    globalThis as {
      PublicKeyCredential?: {
        isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
      };
    }
  ).PublicKeyCredential;
  const probe = publicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable;

  return {
    credentials: globalThis.navigator?.credentials as
      | WebAuthnCredentialContainer
      | undefined,
    isUserVerifyingPlatformAuthenticatorAvailable:
      probe === undefined ? undefined : () => probe.call(publicKeyCredential),
    randomBytes: (length) => globalThis.crypto.getRandomValues(new Uint8Array(length)),
    relyingPartyId: undefined,
  };
}

export interface WebAuthnVerificationOptions {
  readonly environment?: WebAuthnEnvironment;
  /** Where the enrolled credential id is kept. Shared across the profile's tabs. */
  readonly store: StringKeyValueStore;
}

/**
 * A `DeviceVerificationPort` backed by the platform authenticator.
 *
 * Enrolment happens inside `verify` rather than through a separate method, and
 * that is a deliberate contract choice: `navigator.credentials.create()` with
 * `userVerification: "required"` runs the *same* owner gesture that `get()`
 * does, so the ceremony that enrols is itself a verification. It means the
 * first prompt a user ever sees — the one raised to turn Journal Lock on — is
 * the enrolment, which is exactly when `02-ARCHITECTURE.md` §6.2 says the
 * credential is created, and no surface has to know which of the two ceremonies
 * it is about to trigger.
 */
export class WebAuthnDeviceVerification implements DeviceVerificationPort {
  readonly #environment: WebAuthnEnvironment;
  readonly #store: StringKeyValueStore;
  readonly #reasons: JournalAuthReason[] = [];

  constructor(options: WebAuthnVerificationOptions) {
    this.#environment = options.environment ?? browserWebAuthnEnvironment();
    this.#store = options.store;
  }

  async availability(): Promise<WebVerificationAvailability> {
    const probe = this.#environment.isUserVerifyingPlatformAuthenticatorAvailable;
    if (probe === undefined || this.#environment.credentials === undefined) {
      return "unavailable";
    }
    try {
      return (await probe()) ? "passcode" : "unavailable";
    } catch {
      // A probe that throws is a browser that cannot answer, which is the same
      // fact as "no authenticator" for every decision downstream. Fail closed.
      return "unavailable";
    }
  }

  async verify(reason: JournalAuthReason): Promise<boolean> {
    // Recorded even though WebAuthn cannot display it: the reason is what the
    // curtain says, and keeping the record here is what lets a test prove the
    // right ceremony was raised for the right cause.
    this.#reasons.push(reason);

    const credentials = this.#environment.credentials;
    if (credentials === undefined) return false;
    if ((await this.availability()) === "unavailable") return false;

    const enrolled = this.#enrolledCredentialId();
    if (enrolled === null) return this.#enrol(credentials);

    try {
      const assertion = await credentials.get({
        publicKey: {
          challenge: this.#environment.randomBytes(32),
          // Scoped to the one credential this app enrolled. Without it a
          // discoverable credential from another purpose could satisfy the
          // ceremony, and the gate would be verifying the wrong thing.
          allowCredentials: [
            { type: "public-key", id: enrolled, transports: ["internal"] },
          ],
          userVerification: "required",
          timeout: WEBAUTHN_TIMEOUT_MS,
          ...(this.#environment.relyingPartyId === undefined
            ? {}
            : { rpId: this.#environment.relyingPartyId }),
        },
      });
      return assertion !== null;
    } catch (error) {
      // `NotAllowedError` is the user declining *or* timing out, and declining
      // is not an error (`JOURNAL_AUTH_CANCEL_TITLE` is "Not now"). It must not
      // trigger a re-enrolment: a second system sheet immediately after a
      // decline reads as the app refusing to take no for an answer.
      //
      // Any *other* name means the stored id no longer resolves to a usable
      // credential — the user cleared it in OS settings, or the authenticator
      // was reset. Forgetting it here is what keeps that recoverable: the next
      // deliberate attempt enrols a fresh credential instead of failing forever
      // against an id that will never work again.
      if (!isUserDeclined(error)) this.#store.remove(WEBAUTHN_CREDENTIAL_KEY);
      return false;
    }
  }

  /** Reasons a ceremony was raised for, oldest first. Diagnostics and tests. */
  reasons(): readonly JournalAuthReason[] {
    return [...this.#reasons];
  }

  /** Drops the enrolled credential. Used when a passphrase replaces it. */
  forget(): void {
    this.#store.remove(WEBAUTHN_CREDENTIAL_KEY);
  }

  #enrolledCredentialId(): ByteArray | null {
    const stored = this.#store.read(WEBAUTHN_CREDENTIAL_KEY);
    if (stored === undefined || stored === "") return null;
    const bytes = decodeBase64Url(stored);
    if (bytes === null || bytes.length === 0) {
      // Not a credential id. Nothing can be done with it and leaving it in
      // place would make every future ceremony fail against garbage.
      this.#store.remove(WEBAUTHN_CREDENTIAL_KEY);
      return null;
    }
    return bytes;
  }

  async #enrol(credentials: WebAuthnCredentialContainer): Promise<boolean> {
    try {
      const created = await credentials.create({
        publicKey: {
          challenge: this.#environment.randomBytes(32),
          rp: {
            name: WEBAUTHN_RELYING_PARTY_NAME,
            ...(this.#environment.relyingPartyId === undefined
              ? {}
              : { id: this.#environment.relyingPartyId }),
          },
          // The user handle is random and deliberately not persisted. Nothing
          // in this app looks a user up by it — the credential is always
          // addressed by id through `allowCredentials` — and a stable handle
          // would be a cross-site-stable identifier we have no use for.
          user: {
            id: this.#environment.randomBytes(16),
            name: WEBAUTHN_USER_NAME,
            displayName: WEBAUTHN_USER_NAME,
          },
          pubKeyCredParams: [...CREDENTIAL_ALGORITHMS],
          authenticatorSelection: {
            // Platform only. A roaming security key would satisfy the ceremony
            // while leaving the journal unlockable by whoever holds the key,
            // which is not what "device owner" means.
            authenticatorAttachment: "platform",
            residentKey: "discouraged",
            userVerification: "required",
          },
          // No attestation is requested because none would be verified: there
          // is no server to check it against, and collecting an authenticator
          // identifier we cannot use is a privacy cost with no benefit.
          attestation: "none",
          timeout: WEBAUTHN_TIMEOUT_MS,
        },
      });
      if (created === null) return false;
      const rawId = credentialRawId(created);
      if (rawId === null) return false;
      // A refused write is not fatal to *this* ceremony — the owner was
      // verified — but the credential would be unusable next time, so the
      // enrolment is reported as failed rather than leaving the user with a
      // lock whose only key was thrown away.
      return this.#store.write(WEBAUTHN_CREDENTIAL_KEY, encodeBase64Url(rawId));
    } catch {
      return false;
    }
  }
}

/**
 * Whether the failure was the user saying no.
 *
 * The name is the only signal the spec gives, and both "declined" and "timed
 * out" arrive as `NotAllowedError`. They are treated identically on purpose:
 * every non-`true` outcome lands on the same fail-closed path, and a caller
 * that distinguished them would eventually treat one of them as permission.
 */
function isUserDeclined(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name: unknown }).name === "NotAllowedError"
  );
}

function credentialRawId(credential: Credential): ByteArray | null {
  const raw = (credential as { rawId?: unknown }).rawId;
  if (raw instanceof ArrayBuffer) return new Uint8Array(raw);
  if (ArrayBuffer.isView(raw)) {
    // Copied rather than viewed: the source buffer may be shared, which the
    // platform APIs refuse, and a view would keep the whole credential buffer
    // alive for the lifetime of the id.
    return new Uint8Array(raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength) as ArrayBuffer);
  }
  return null;
}
